import { useLocale } from '@solinkcorp/locale';
import { Button } from '@solinkcorp/solink-ui/components/Button';
import { DialogFooter, DialogHeader, DialogTitle } from '@solinkcorp/solink-ui/components/Dialog';
import { cn } from '@solinkcorp/solink-ui/lib/utils';
import { LayoutTemplate, SquarePlus } from 'lucide-react';
import { SDM_EVENTS } from '../../../constants';
import { useSDMAnalytics } from '../../../contexts/AnalyticsContext';
import { type Controller } from '../../../Controller';
import { useStartLayoutFromTemplate } from '../../../hooks/useStartLayoutFromTemplate';
import { getPredefinedLayoutById, type PredefinedLayout } from '../../../layout/predefinedLayouts';
import { PredefinedLayoutGallery } from './PredefinedLayoutGallery';

const primaryButtonClasses = (disabled: boolean) =>
  cn(
    'border-none',
    disabled
      ? 'cursor-not-allowed bg-interaction-primary-background-inactive text-interaction-primary-content-inactive'
      : 'bg-interaction-primary-background-default text-interaction-primary-content-default hover:bg-interaction-primary-background-hover',
  );

const cancelButtonClasses =
  'border-border-muted text-interaction-neutral-content-default hover:bg-background-elevated';

export type PickerStep = 'choose' | 'gallery';
export type StartingPoint = 'blank' | 'template' | null;

interface TemplatePickerStepProps {
  controller: Controller;
  step: PickerStep;
  setStep: (step: PickerStep) => void;
  selected: StartingPoint;
  setSelected: (selected: StartingPoint) => void;
  selectedTemplateId: string | null;
  setSelectedTemplateId: (id: string | null) => void;
}

export function TemplatePickerStep({
  controller,
  step,
  setStep,
  selected,
  setSelected,
  selectedTemplateId,
  setSelectedTemplateId,
}: TemplatePickerStepProps) {
  const locale = useLocale();
  const analytics = useSDMAnalytics();
  const startLayoutFromTemplate = useStartLayoutFromTemplate(controller);

  const handleClose = () => {
    controller.hideTemplatePicker();
  };

  const handleNext = () => {
    if (selected === 'blank') {
      analytics.trackSDMEvent(SDM_EVENTS.SELECT_BLANK_LAYOUT, {
        description: 'Selected blank layout from template picker',
      });
      controller.displayLayoutDialog(true, undefined, locale.serviceDisplayMonitor.layout);
    } else if (selected === 'template') {
      setStep('gallery');
    }
  };

  const handleTemplateSelect = async (template: PredefinedLayout) => {
    analytics.trackSDMEvent(SDM_EVENTS.SELECT_TEMPLATE, {
      description: 'Selected a predefined layout template',
      templateId: template.id,
      templateName: template.name,
      templateCategory: template.category,
      widgetCount: template.widgets.length,
    });
    await startLayoutFromTemplate(template);
  };

  const handleGalleryNext = async () => {
    if (!selectedTemplateId) return;
    const template = getPredefinedLayoutById(selectedTemplateId);
    if (template) await handleTemplateSelect(template);
  };

  return (
    <>
      <DialogHeader>
        <DialogTitle>
          {step === 'gallery'
            ? locale.serviceDisplayMonitor.selectTemplate
            : locale.serviceDisplayMonitor.newLayout}
        </DialogTitle>
      </DialogHeader>

      {step === 'choose' ? (
        <>
          <div className="flex flex-col gap-3">
            <OptionCard
              icon={<SquarePlus className="size-4" />}
              title={locale.serviceDisplayMonitor.blankLayout}
              description={locale.serviceDisplayMonitor.blankLayoutDescription}
              selected={selected === 'blank'}
              onClick={() => setSelected('blank')}
              data-testid="option-blank"
            />
            <OptionCard
              icon={<LayoutTemplate className="size-4" />}
              title={locale.serviceDisplayMonitor.startFromTemplate}
              description={locale.serviceDisplayMonitor.startFromTemplateDescription}
              selected={selected === 'template'}
              onClick={() => setSelected('template')}
              data-testid="option-template"
            />
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={handleClose}
              type="button"
              className={cancelButtonClasses}
              data-testid="template-picker-cancel-btn"
            >
              {locale.general.cancel}
            </Button>
            <Button
              onClick={handleNext}
              type="button"
              disabled={selected === null}
              className={primaryButtonClasses(selected === null)}
              data-testid="template-picker-next-btn"
            >
              {locale.general.next}
            </Button>
          </DialogFooter>
        </>
      ) : (
        <>
          <div className="flex min-h-0 flex-1 flex-col">
            <PredefinedLayoutGallery
              selectedId={selectedTemplateId}
              onSelect={(template) => setSelectedTemplateId(template.id)}
            />
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setStep('choose')}
              type="button"
              className={cancelButtonClasses}
              data-testid="template-picker-back-btn"
            >
              {locale.general.back}
            </Button>
            <Button
              onClick={handleGalleryNext}
              type="button"
              disabled={selectedTemplateId === null}
              className={primaryButtonClasses(selectedTemplateId === null)}
              data-testid="template-picker-confirm-btn"
            >
              {locale.serviceDisplayMonitor.nextLayoutDetails}
            </Button>
          </DialogFooter>
        </>
      )}
    </>
  );
}

interface OptionCardProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  selected: boolean;
  onClick: () => void;
  'data-testid'?: string;
}

function OptionCard({
  icon,
  title,
  description,
  selected,
  onClick,
  'data-testid': testId,
}: OptionCardProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={cn(
        'flex w-full items-start gap-4 rounded-lg border p-4 text-left transition-colors',
        'bg-background',
        selected ? 'border-2 border-primary' : 'm-[1px] border-border-muted',
      )}
    >
      <div className="shrink-0 pt-1">
        <div
          className={cn(
            'flex size-8 items-center justify-center rounded',
            selected
              ? 'bg-background-accent text-foreground-accent'
              : 'bg-background-elevated text-interaction-neutral-content-default',
          )}
        >
          {icon}
        </div>
      </div>

      <div className="flex flex-col gap-0.5">
        <span className="text-text-primary heading-sm">{title}</span>
        <span className="text-foreground-secondary body-md">{description}</span>
      </div>
    </button>
  );
}
